/* eslint-disable react/prop-types */
/**
 * AttachmentsTab — files attached to any of 7 parent types: deal,
 * lease_deed, lease_deed_tract, well, well_tract, payment, remark.
 *
 * Same Radio.Group + secondary Select scope picker as RemarksTab, with
 * `remark` added.
 *
 * Why not WigMetadataCrudPanel? It's optimized for CRUD-via-modal-form. We
 * need a file picker, async upload progress, a per-row Download (separate
 * SAS-URL fetch), and per-row Delete (no Edit). A focused custom component
 * with AntD `Upload` + `Table` is cleaner.
 *
 * The legacy `attachmentsPanel.jsx` (v1 `/broker_deals/{deal_key}/
 * attachments` endpoints) was removed with the Pipeline / Density pages.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  Alert,
  Button,
  Card,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
  Upload,
  message,
  notification,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { format as numerableFormat } from "numerable";

import { WigAntTable } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import {
  ATTACHMENT_MAX_LABEL,
  attachmentSizeError,
} from "../../../utils/attachmentLimits";
import pickListsService from "../../../services/pickListsService";

const { Dragger } = Upload;
const { Text } = Typography;

// parent_table values supported by the backend polymorphic validator for
// attachments. Order = display order in the radio.
const PARENT_OPTIONS = [
  { value: "deal", label: "Deal" },
  { value: "lease_deed", label: "Lease/Deed" },
  { value: "lease_deed_tract", label: "Lease Tract" },
  { value: "well", label: "Well" },
  { value: "well_tract", label: "Well Tract" },
  { value: "payment", label: "Payment" },
  { value: "remark", label: "Remark" },
];

const PARENT_VALUES = new Set(PARENT_OPTIONS.map((o) => o.value));

// Where each non-deal parent record is created, and what to call it.
//
// UAT: testers reported that "attachment upload is limited to the Deal parent
// type" and that a role "has no Add option under Lease/Deed attachments". Both
// were the same dead end — an attachment hangs off an EXISTING record, so when
// a deal has no lease/deeds (or no wells, tracts, payments...) the Entity list
// comes back empty, the whole uploader disappeared, and the tester read that as
// a permission or scope limitation. This map lets the empty state say what is
// actually missing and which tab creates it.
const PARENT_SOURCE = {
  lease_deed: { noun: "lease/deed", tab: "Lease/Deeds" },
  lease_deed_tract: { noun: "lease tract", tab: "Lease/Deeds" },
  well: { noun: "well", tab: "Wells" },
  well_tract: { noun: "well tract", tab: "Wells" },
  payment: { noun: "payment", tab: "Payments" },
  remark: { noun: "remark", tab: "Remarks" },
};

const parentNoun = (t) => PARENT_SOURCE[t]?.noun || String(t).replace(/_/g, " ");

const leaseDeedLabel = (ld) => {
  if (!ld) return "";
  const num = ld.external_number || `#${ld.id}`;
  const grantor = ld.lessor_grantor ? ` — ${ld.lessor_grantor}` : "";
  return `${num}${grantor}`;
};
const wellLabel = (w) => {
  if (!w) return "";
  const name = w.well_name || `Well #${w.id}`;
  const api = w.api_10 ? ` — API ${w.api_10}` : "";
  return `${name}${api}`;
};
const tractLabel = (t) => {
  if (!t) return "";
  const loc = t.location_type ? `${t.location_type} ` : "";
  return `${loc}Tract #${t.id}`;
};
const paymentLabel = (p) => {
  if (!p) return "";
  const t = p.payment_type || "Payment";
  const amt = p.amount != null ? ` — $${p.amount}` : "";
  return `${t}${amt} #${p.id}`;
};
const remarkLabel = (r) => {
  if (!r) return "";
  const body = (r.body || r.text || "").slice(0, 60);
  return `#${r.id}${body ? ` — ${body}` : ""}`;
};

// UAT #24 — enumerate every attachment parent under a deal (the deal itself
// plus each lease/deed, lease tract, well, well tract, payment, and remark) so
// the "view all" toggle can aggregate attachments across the whole deal. There
// is no deal-wide attachments endpoint, so we fan out one list call per parent
// (same tradeoff as the whole-deal child views). Each entry carries a friendly
// scope label for the aggregated grid's Scope column.
const collectDealParents = async (token, userOid, dealId) => {
  const safe = (p) => p.then((r) => r?.data?.data || []).catch(() => []);
  const parents = [{ parentTable: "deal", parentId: dealId, scopeLabel: "Deal" }];

  const lds = await safe(dealsService.listLeaseDeeds({ token, userOid, dealId }));
  for (const ld of lds) {
    parents.push({
      parentTable: "lease_deed",
      parentId: ld.id,
      scopeLabel: `Lease/Deed ${leaseDeedLabel(ld)}`,
    });
  }
  const ldTracts = (
    await Promise.all(
      lds.map((ld) =>
        safe(
          dealsService.listLeaseDeedTracts({ token, userOid, leaseDeedId: ld.id })
        ).then((ts) => ts.map((t) => ({ ld, t })))
      )
    )
  ).flat();
  for (const { ld, t } of ldTracts) {
    parents.push({
      parentTable: "lease_deed_tract",
      parentId: t.id,
      scopeLabel: `Lease Tract ${leaseDeedLabel(ld)} / #${t.id}`,
    });
  }

  const ws = await safe(dealsService.listWells({ token, userOid, dealId }));
  for (const w of ws) {
    parents.push({
      parentTable: "well",
      parentId: w.id,
      scopeLabel: `Well ${wellLabel(w)}`,
    });
  }
  const wTracts = (
    await Promise.all(
      ws.map((w) =>
        safe(dealsService.listWellTracts({ token, userOid, wellId: w.id })).then(
          (ts) => ts.map((t) => ({ w, t }))
        )
      )
    )
  ).flat();
  for (const { w, t } of wTracts) {
    parents.push({
      parentTable: "well_tract",
      parentId: t.id,
      scopeLabel: `Well Tract ${wellLabel(w)} / ${tractLabel(t)}`,
    });
  }

  const pays = await safe(
    dealsService.listPayments({ token, userOid, parentTable: "deal", parentId: dealId })
  );
  for (const p of pays) {
    parents.push({
      parentTable: "payment",
      parentId: p.id,
      scopeLabel: `Payment ${paymentLabel(p)}`,
    });
  }
  const rems = await safe(
    dealsService.listRemarks({ token, userOid, parentTable: "deal", parentId: dealId })
  );
  for (const r of rems) {
    parents.push({
      parentTable: "remark",
      parentId: r.id,
      scopeLabel: `Remark ${remarkLabel(r)}`,
    });
  }
  return parents;
};

// Normalize an axios/FastAPI error into a plain string. A 422 body is
// `{ detail: [{ loc, msg, type }, ...] }` — passing that array straight
// into a notification/Alert throws "Objects are not valid as a React child".
const errText = (err, fallback) => apiErrorText(err, { fallback });

const humanBytes = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return "—";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${numerableFormat(v / 1024, "0,0.0")} KB`;
  if (v < 1024 * 1024 * 1024)
    return `${numerableFormat(v / (1024 * 1024), "0,0.0")} MB`;
  return `${numerableFormat(v / (1024 * 1024 * 1024), "0,0.00")} GB`;
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return String(iso);
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.round(day / 365);
  return `${yr}y ago`;
};

const AttachmentsTab = ({ dealId, dealKey }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  // POST/DELETE /attachments are [BrokerAgent, Landman, LandTech, TitleAgent,
  // Accounting] — `attachments.upload` mirrors that list exactly. Roles
  // outside it (CapexApprover, LeaseRecords, DivisionOrders) keep read +
  // download; the uploader and the row Delete disappear rather than 403.
  const { can, canUploadAttachmentType } = usePermissions();
  const mayUpload = can("attachments.upload");

  // Scope state. The initial scope can be deep-linked
  // (`?tab=attachments&parent_table=lease_deed&parent_id=12`) — that is how the
  // per-record Attachments buttons on the Lease/Deeds and Wells tabs land here
  // already pointed at the right lease, tract or well (UAT: attaching below the
  // deal level was reachable but undiscoverable).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParentTable = searchParams.get("parent_table");
  const initialParentTable = PARENT_VALUES.has(urlParentTable)
    ? urlParentTable
    : "deal";
  // `parent_id` accepts a comma-separated list, so a multi-record selection
  // survives a reload or a trip through another tab.
  const initialParentIds = String(searchParams.get("parent_id") || "")
    .split(",")
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
  const [parentTable, setParentTable] = useState(initialParentTable);
  const [parentEntities, setParentEntities] = useState([]);
  // The records this tab is pointed at. One or MANY — picking several
  // lease/deeds lists their attachments together and files a dropped file
  // against each of them (the API is one parent per attachment, so that is one
  // POST per selected record).
  const [selectedParentIds, setSelectedParentIds] = useState(
    initialParentTable === "deal" ? [dealId] : initialParentIds
  );
  const [scopeLoading, setScopeLoading] = useState(false);
  // UAT #24: aggregate every attachment across the deal + all its children.
  const [viewAll, setViewAll] = useState(false);

  // Grid state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Attachment-type pick-list (UAT #17). A category chosen here is required
  // before any file can be uploaded and is stored per-attachment.
  const [allTypeOptions, setAllTypeOptions] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  // Some document categories are narrower than `attachments.upload` — a
  // BD.BrokerAgent may not file a TITLE OPINION, which is the land team's /
  // title examiner's work product. Drop those rows from the dropdown so the
  // role never sees the option (see ATTACHMENT_TYPE_ACTIONS in
  // utils/permissions.js).
  const typeOptions = useMemo(
    () => allTypeOptions.filter((o) => canUploadAttachmentType(o.value)),
    [allTypeOptions, canUploadAttachmentType]
  );

  // id -> friendly label for the records currently listed in the picker.
  // Used to tag rows when several records are selected and to name a record in
  // a partial-upload failure.
  const entityLabelById = useMemo(
    () => new Map(parentEntities.map((e) => [e.id, e.label])),
    [parentEntities]
  );

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  // Load the attachment-type vocabulary once. Same pick-list surface the
  // Admin → Pick Lists page manages, filtered to
  // (table_name='attachments', field='attachment_type').
  useEffect(() => {
    let cancelled = false;
    const loadTypes = async () => {
      setTypesLoading(true);
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await pickListsService.listPickLists({
          token: tk,
          userOid,
          tableName: "attachments",
          field: "attachment_type",
        });
        const rows = resp?.data?.data || resp?.data || [];
        const opts = rows
          .slice()
          .sort((a, b) => {
            const s = (a?.sort_order ?? 0) - (b?.sort_order ?? 0);
            if (s !== 0) return s;
            return String(a?.value ?? "").localeCompare(String(b?.value ?? ""));
          })
          .map((r) => ({ value: r.value, label: r.label || r.value }));
        if (cancelled) return;
        setAllTypeOptions(opts);
      } catch (err) {
        if (cancelled) return;
        console.error("AttachmentsTab type pick-list load failed:", err);
      } finally {
        if (!cancelled) setTypesLoading(false);
      }
    };
    loadTypes();
    return () => {
      cancelled = true;
    };
  }, [getAuth]);

  // Load the parent-entity list for the currently selected parent_table.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!dealId) return;
      setScopeLoading(true);
      try {
        const { token: tk, userOid } = await getAuth();
        let entities = [];

        if (parentTable === "deal") {
          entities = [{ id: dealId, label: `Current Deal (${dealKey || dealId})` }];
        } else if (parentTable === "lease_deed") {
          const resp = await dealsService.listLeaseDeeds({
            token: tk,
            userOid,
            dealId,
          });
          entities = (resp?.data?.data || []).map((ld) => ({
            id: ld.id,
            label: leaseDeedLabel(ld),
          }));
        } else if (parentTable === "lease_deed_tract") {
          const ldResp = await dealsService.listLeaseDeeds({
            token: tk,
            userOid,
            dealId,
          });
          const lds = ldResp?.data?.data || [];
          const tractLists = await Promise.all(
            lds.map((ld) =>
              dealsService
                .listLeaseDeedTracts({
                  token: tk,
                  userOid,
                  leaseDeedId: ld.id,
                })
                .then((r) => (r?.data?.data || []).map((t) => ({ ld, t })))
                .catch(() => [])
            )
          );
          entities = tractLists.flat().map(({ ld, t }) => ({
            id: t.id,
            label: `${leaseDeedLabel(ld)} / Tract #${t.id}`,
          }));
        } else if (parentTable === "well") {
          const resp = await dealsService.listWells({
            token: tk,
            userOid,
            dealId,
          });
          entities = (resp?.data?.data || []).map((w) => ({
            id: w.id,
            label: wellLabel(w),
          }));
        } else if (parentTable === "well_tract") {
          const wResp = await dealsService.listWells({
            token: tk,
            userOid,
            dealId,
          });
          const ws = wResp?.data?.data || [];
          const tractLists = await Promise.all(
            ws.map((w) =>
              dealsService
                .listWellTracts({ token: tk, userOid, wellId: w.id })
                .then((r) => (r?.data?.data || []).map((t) => ({ w, t })))
                .catch(() => [])
            )
          );
          entities = tractLists.flat().map(({ w, t }) => ({
            id: t.id,
            label: `${wellLabel(w)} / ${tractLabel(t)}`,
          }));
        } else if (parentTable === "payment") {
          const resp = await dealsService.listPayments({
            token: tk,
            userOid,
            parentTable: "deal",
            parentId: dealId,
          });
          entities = (resp?.data?.data || []).map((p) => ({
            id: p.id,
            label: paymentLabel(p),
          }));
        } else if (parentTable === "remark") {
          const resp = await dealsService.listRemarks({
            token: tk,
            userOid,
            parentTable: "deal",
            parentId: dealId,
          });
          entities = (resp?.data?.data || []).map((r) => ({
            id: r.id,
            label: remarkLabel(r),
          }));
        }

        if (cancelled) return;
        setParentEntities(entities);
        setSelectedParentIds((prev) => {
          if (parentTable === "deal") return [dealId];
          // Keep whatever of the previous selection still exists under the new
          // parent type (the deep-linked id lands here); otherwise start on the
          // first record so the grid isn't empty for no visible reason.
          const kept = prev.filter((id) => entities.some((e) => e.id === id));
          if (kept.length) return kept;
          return entities[0]?.id != null ? [entities[0].id] : [];
        });
      } catch (err) {
        if (cancelled) return;
        console.error("AttachmentsTab scope load failed:", err);
        setErrMsg(apiErrorText(err));
      } finally {
        if (!cancelled) setScopeLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [parentTable, dealId, dealKey, getAuth]);

  // Load attachments for the active (parent_table, parent_id) — or, when
  // "view all" is on (UAT #24), aggregate across the deal and every child.
  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();

      if (viewAll) {
        if (!dealId) {
          setItems([]);
          return;
        }
        const parents = await collectDealParents(tk, userOid, dealId);
        const results = await Promise.allSettled(
          parents.map((p) =>
            dealsService.listAttachments({
              token: tk,
              userOid,
              parentTable: p.parentTable,
              parentId: p.parentId,
            })
          )
        );
        const all = [];
        results.forEach((res, i) => {
          if (res.status !== "fulfilled") return;
          const rows = res.value?.data?.data || res.value?.data || [];
          for (const row of rows) all.push({ ...row, __scope: parents[i].scopeLabel });
        });
        setItems(all);
        return;
      }

      if (!selectedParentIds.length) {
        setItems([]);
        return;
      }
      if (selectedParentIds.length === 1) {
        const resp = await dealsService.listAttachments({
          token: tk,
          userOid,
          parentTable,
          parentId: selectedParentIds[0],
        });
        setItems(resp?.data?.data || resp?.data || []);
        return;
      }
      // Several records selected — list them together, tagged with which
      // record each file hangs off (same Scope column the "view all" toggle
      // uses). One call per record: there is no multi-parent list endpoint.
      const results = await Promise.allSettled(
        selectedParentIds.map((id) =>
          dealsService.listAttachments({
            token: tk,
            userOid,
            parentTable,
            parentId: id,
          })
        )
      );
      const merged = [];
      results.forEach((res, i) => {
        if (res.status !== "fulfilled") return;
        const rows = res.value?.data?.data || res.value?.data || [];
        const label = entityLabelById.get(selectedParentIds[i]);
        for (const row of rows) merged.push({ ...row, __scope: label });
      });
      setItems(merged);
    } catch (err) {
      console.error("AttachmentsTab list failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [viewAll, parentTable, selectedParentIds, entityLabelById, dealId, getAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleUpload = useCallback(
    async (file) => {
      // Nothing selected = nothing to associate the file with. The Dragger is
      // disabled in that state; this guards a stray drop.
      if (!selectedParentIds.length) {
        message.warning(
          `Select the ${parentNoun(parentTable)}(s) this file belongs to first.`
        );
        return false;
      }
      // Type is required (UAT #17) — the Dragger is disabled without one, but
      // guard here too so a stray drop can't skip the category.
      if (!selectedType) {
        message.warning("Select a Type before uploading.");
        return false;
      }
      // Restricted categories are already filtered out of the dropdown; this
      // catches a stale selection (e.g. the pick-list reloading after a role
      // change) rather than posting a type this role may not file.
      if (!canUploadAttachmentType(selectedType)) {
        message.warning(`Your role cannot upload ${selectedType} documents.`);
        return false;
      }
      // Size ceiling, checked before the POST. Without this the request dies at
      // nginx (client_max_body_size) as a bare 413, which reaches the user as
      // "That file is too large to upload" — no size, no limit, nothing to act
      // on. Rejecting here names both numbers and saves the upload entirely.
      const tooBig = attachmentSizeError(file);
      if (tooBig) {
        setErrMsg(tooBig);
        notification.error({ message: "Upload failed", description: tooBig });
        return false;
      }
      setUploading(true);
      setErrMsg(null);
      try {
        const { token: tk, userOid } = await getAuth();
        // One POST per selected record — an attachment row carries exactly one
        // (parent_table, parent_id), so associating a file with three
        // lease/deeds means three rows (and three blobs). Sequential on
        // purpose: partial success has to be reportable per record.
        const failures = [];
        for (const parentId of selectedParentIds) {
          try {
            await dealsService.uploadAttachment({
              token: tk,
              userOid,
              parentTable,
              parentId,
              file,
              attachmentType: selectedType,
            });
          } catch (err) {
            failures.push({
              label: entityLabelById.get(parentId) || `#${parentId}`,
              text: errText(err, "Upload failed"),
            });
          }
        }
        const ok = selectedParentIds.length - failures.length;
        if (ok > 0) {
          message.success(
            selectedParentIds.length > 1
              ? `Uploaded ${file.name} to ${ok} ${parentNoun(parentTable)}${
                  ok === 1 ? "" : "s"
                }`
              : `Uploaded ${file.name}`
          );
        }
        if (failures.length) {
          notification.error({
            message: `Upload failed for ${failures.length} ${parentNoun(
              parentTable
            )}${failures.length === 1 ? "" : "s"}`,
            description: failures.map((f) => `${f.label}: ${f.text}`).join(" · "),
          });
        }
        await refresh();
      } catch (err) {
        notification.error({
          message: "Upload failed",
          description: errText(err, "Upload failed"),
        });
      } finally {
        setUploading(false);
      }
      // false = tell AntD <Upload> not to POST itself; we handled it.
      return false;
    },
    [
      parentTable,
      selectedParentIds,
      entityLabelById,
      selectedType,
      canUploadAttachmentType,
      getAuth,
      refresh,
    ]
  );

  const handleDownload = useCallback(
    async (att) => {
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.getAttachmentUrl({
          token: tk,
          userOid,
          id: att.id,
        });
        const url = resp?.data?.url ?? resp?.data?.data?.url ?? null;
        if (!url) {
          message.error("Could not resolve download URL");
          return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        message.error(errText(err, "Download failed"));
      }
    },
    [getAuth]
  );

  const handleDelete = useCallback(
    async (att) => {
      setDeletingId(att.id);
      try {
        const { token: tk, userOid } = await getAuth();
        await dealsService.deleteAttachment({
          token: tk,
          userOid,
          id: att.id,
        });
        message.success(`Deleted ${att.filename}`);
        await refresh();
      } catch (err) {
        message.error(errText(err, "Delete failed"));
      } finally {
        setDeletingId(null);
      }
    },
    [getAuth, refresh]
  );

  const parentOptions = useMemo(
    () => parentEntities.map((e) => ({ value: e.id, label: e.label })),
    [parentEntities]
  );

  // Mirror the active scope back into the query string so a reload, a
  // bookmark, or a trip through another tab (the Tabs are
  // `destroyInactiveTabPane`, so this pane remounts) comes back to the same
  // record instead of snapping to the deal.
  const syncScopeToUrl = useCallback(
    (table, ids) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", "attachments");
      if (table === "deal") {
        next.delete("parent_table");
        next.delete("parent_id");
      } else {
        next.set("parent_table", table);
        if (!ids?.length) next.delete("parent_id");
        else next.set("parent_id", ids.join(","));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onParentTableChange = useCallback(
    (value) => {
      setParentTable(value);
      // The records are resolved by the scope-loading effect (whatever of the
      // old selection survives, else the first row), so drop the stale ids.
      syncScopeToUrl(value, []);
    },
    [syncScopeToUrl]
  );

  const onSelectedParentChange = useCallback(
    (value) => {
      // AntD hands back an array in `multiple` mode and a scalar for the deal.
      const ids = (Array.isArray(value) ? value : [value]).filter(
        (v) => v != null
      );
      setSelectedParentIds(ids);
      syncScopeToUrl(parentTable, ids);
    },
    [parentTable, syncScopeToUrl]
  );

  // True when the chosen parent type has no rows on this deal at all — the
  // case that used to render as a bare "Select an entity above".
  const noParentRecords =
    !viewAll && parentTable !== "deal" && !scopeLoading && !parentOptions.length;

  const multiParent = parentTable !== "deal";
  const selectionCount = selectedParentIds.length;
  // Show the Scope column whenever a row's parent isn't implied by the picker.
  const showScopeColumn = viewAll || selectionCount > 1;

  const columns = useMemo(() => {
    const cols = [
      {
        title: "File",
        dataIndex: "filename",
        key: "filename",
        sorter: (a, b) =>
          String(a?.filename ?? "").localeCompare(String(b?.filename ?? "")),
        render: (v, att) => {
          const text = <Text style={{ wordBreak: "break-all" }}>{v}</Text>;
          // Preserve the raw MIME as a tooltip on the filename now that the
          // visible Type column shows the chosen category instead (UAT #17).
          return att?.content_type ? (
            <Tooltip title={att.content_type}>{text}</Tooltip>
          ) : (
            text
          );
        },
      },
      {
        title: "Type",
        dataIndex: "attachment_type",
        key: "attachment_type",
        width: 220,
        sorter: (a, b) =>
          String(a?.attachment_type ?? "").localeCompare(
            String(b?.attachment_type ?? "")
          ),
        render: (v) => v || <Text type="secondary">—</Text>,
      },
      {
        title: "Size",
        dataIndex: "size_bytes",
        key: "size_bytes",
        width: 100,
        sorter: (a, b) => Number(a?.size_bytes ?? 0) - Number(b?.size_bytes ?? 0),
        render: (v) => humanBytes(v),
      },
      {
        title: "Uploaded by",
        dataIndex: "uploaded_by",
        key: "uploaded_by",
        width: 200,
        sorter: (a, b) =>
          String(a?.uploaded_by ?? "").localeCompare(
            String(b?.uploaded_by ?? "")
          ),
        render: (v) => v || <Text type="secondary">—</Text>,
      },
      {
        // Sorted on the raw timestamp — the cell shows "3 days ago", which
        // would sort alphabetically and mean nothing.
        title: "Uploaded",
        dataIndex: "uploaded_at",
        key: "uploaded_at",
        width: 140,
        sorter: (a, b) =>
          String(a?.uploaded_at ?? "").localeCompare(
            String(b?.uploaded_at ?? "")
          ),
        render: (v) =>
          v ? relativeTime(v) : <Text type="secondary">—</Text>,
      },
      {
        title: "",
        key: "actions",
        width: 100,
        align: "right",
        render: (_, att) => (
          <Space size={4}>
            <Button
              size="small"
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(att)}
              aria-label={`Download ${att.filename}`}
            />
            {mayUpload && (
              <Popconfirm
                title="Delete attachment?"
                description={`This permanently removes ${att.filename}.`}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                onConfirm={() => handleDelete(att)}
              >
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingId === att.id}
                  aria-label={`Delete ${att.filename}`}
                />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ];
    // Show which record each file hangs off whenever the picker doesn't imply
    // it — "view all" mode, or several records selected at once.
    if (showScopeColumn) {
      cols.splice(1, 0, {
        title: "Scope",
        dataIndex: "__scope",
        key: "__scope",
        width: 220,
        sorter: (a, b) =>
          String(a?.__scope ?? "").localeCompare(String(b?.__scope ?? "")),
        render: (v) => v || <Text type="secondary">—</Text>,
      });
    }
    return cols;
  }, [handleDownload, handleDelete, deletingId, showScopeColumn, mayUpload]);

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {errMsg && (
        <Alert
          type="error"
          showIcon
          message="Failed to load attachments"
          description={errMsg}
        />
      )}

      <Card size="small" title="Scope — what you are viewing and attaching to">
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Space align="center" wrap>
            <Switch checked={viewAll} onChange={setViewAll} />
            <Text strong>View all attachments for this deal</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              (aggregates the deal and all its lease/deeds, wells, tracts,
              payments, and remarks — read-only)
            </Text>
          </Space>
          <Space align="center" wrap>
            <Text strong>Parent type:</Text>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={parentTable}
              onChange={(e) => onParentTableChange(e.target.value)}
              options={PARENT_OPTIONS}
              disabled={viewAll}
            />
          </Space>
          {/* The record picker. Multi-select for every parent type below the
              deal (a deal has exactly one row, so that stays fixed): pick one
              or several lease/deeds, tracts, wells, payments or remarks. The
              selection is BOTH what the grid lists and what a dropped file gets
              associated with — UAT could not tell that the old single "Entity"
              select was the upload target at all. */}
          <Space align="start" wrap>
            <Text strong style={{ lineHeight: "32px" }}>
              {multiParent
                ? `${PARENT_OPTIONS.find((o) => o.value === parentTable)?.label}(s):`
                : "Deal:"}
            </Text>
            <Space direction="vertical" size={2}>
              <Select
                mode={multiParent ? "multiple" : undefined}
                value={multiParent ? selectedParentIds : selectedParentIds[0]}
                options={parentOptions}
                onChange={onSelectedParentChange}
                placeholder={
                  multiParent
                    ? `Select the ${parentNoun(parentTable)}(s) to attach to`
                    : "Current deal"
                }
                style={{ minWidth: 420, maxWidth: 640 }}
                loading={scopeLoading}
                disabled={viewAll || !multiParent || !parentOptions.length}
                allowClear={multiParent}
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                data-testid="attachment-parent-select"
                aria-label="Records to attach to"
              />
              {multiParent && !viewAll && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {selectionCount > 1
                    ? `Files uploaded below are filed against all ${selectionCount} selected ${parentNoun(
                        parentTable
                      )}s.`
                    : `Select more than one ${parentNoun(
                        parentTable
                      )} to file the same document against each of them.`}
                </Text>
              )}
            </Space>
          </Space>
        </Space>
      </Card>

      <Card size="small" title={`Attachments (${items.length})`}>
        {/* The chosen parent type has no rows on this deal. Previously this
            rendered as "Select an entity above to view its attachments" with
            the uploader hidden, which UAT read as "this level cannot take
            attachments" / "my role has no Add option". Name the missing record
            and the tab that creates it instead. */}
        {noParentRecords && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`This deal has no ${parentNoun(parentTable)} records yet`}
            description={
              <>
                Files attach to an existing record, so there is nothing to
                attach to at this level yet. Add a {parentNoun(parentTable)} on
                the <Text strong>{PARENT_SOURCE[parentTable]?.tab}</Text> tab,
                then use its <Text strong>Attachments</Text> button to come
                straight back here with that record selected.
              </>
            }
          />
        )}
        {!viewAll && !mayUpload && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Your role can view and download attachments, but not upload or delete them."
          />
        )}
        {!viewAll && mayUpload && !noParentRecords && (
          <>
            <Space
              align="center"
              wrap
              style={{ marginBottom: 12, width: "100%" }}
            >
              <Text strong>
                Type <Text type="danger">*</Text>:
              </Text>
              <Select
                value={selectedType}
                options={typeOptions}
                onChange={setSelectedType}
                placeholder="Select a Type"
                style={{ minWidth: 280 }}
                loading={typesLoading}
                showSearch
                optionFilterProp="label"
                allowClear
                status={selectedType ? undefined : "warning"}
                data-testid="attachment-type-select"
                aria-label="Attachment Type"
              />
              {!selectedType && (
                <Text type="warning" style={{ fontSize: 12 }}>
                  Select a Type first
                </Text>
              )}
            </Space>

            {!selectionCount && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`Select the ${parentNoun(
                  parentTable
                )}(s) above to associate an upload with`}
              />
            )}

            <Dragger
              name="file"
              multiple
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={uploading || !selectedType || !selectionCount}
              style={{ marginBottom: 12, padding: 6 }}
              data-testid="attachments-upload"
            >
              <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
                <InboxOutlined />
              </p>
              <p className="ant-upload-text" style={{ margin: 0, fontSize: 13 }}>
                {!selectionCount
                  ? `Select the ${parentNoun(
                      parentTable
                    )}(s) above to enable uploads`
                  : !selectedType
                    ? "Select a Type first to enable uploads"
                    : selectionCount > 1
                      ? `Click or drag files here — each file is filed against all ${selectionCount} selected ${parentNoun(
                          parentTable
                        )}s`
                      : "Click or drag files here to upload"}
              </p>
              <p
                className="ant-upload-hint"
                style={{ margin: 0, fontSize: 11, color: "rgba(0,0,0,0.45)" }}
              >
                Multiple files supported. Each uploads on drop. Max{" "}
                {ATTACHMENT_MAX_LABEL} per file.
              </p>
            </Dragger>
          </>
        )}

        <WigAntTable
          size="small"
          rowKey="id"
          dataSource={items}
          columns={columns}
          loading={loading}
          showSearch={false}
          showExport={false}
          showActionButton={false}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{
            emptyText: viewAll
              ? "No attachments on this deal or any of its records yet"
              : selectionCount
                ? "No attachments for this scope yet"
                : `Select the ${parentNoun(
                    parentTable
                  )}(s) above to see their attachments`,
          }}
        />
      </Card>
    </Space>
  );
};

export default AttachmentsTab;
